import type { ScrollAxis } from "./scroll-box-axes"

const SCROLLING_OVERFLOW_VALUES: ReadonlySet<string> = new Set(["auto", "scroll"])
const CLIPPING_OVERFLOW_VALUES: ReadonlySet<string> = new Set(["hidden", "clip"])
const SCROLL_CONTAINER_OVERFLOW_VALUES: ReadonlySet<string> = new Set(["hidden", "auto", "scroll"])
const DISPLAYS_THAT_FORM_NO_CONTAINING_BLOCK: ReadonlySet<string> = new Set([
  "contents",
  "inline",
  "table-row-group",
  "table-header-group",
  "table-footer-group",
  "table-row",
])
const NORMAL_LINE_HEIGHT_TO_FONT_SIZE_RATIO = 1.2

export const OVERFLOW_THRESHOLD_PX = 1
export const COLLAPSED_EXTENT_PX = 1

export function scrollsAlong(element: Element, axis: ScrollAxis): boolean {
  return SCROLLING_OVERFLOW_VALUES.has(getComputedStyle(element)[axis.overflow])
}

export function clipsAlong(element: Element, axis: ScrollAxis): boolean {
  return CLIPPING_OVERFLOW_VALUES.has(getComputedStyle(element)[axis.overflow])
}

export function clipsOrScrollsAlong(element: Element, axis: ScrollAxis): boolean {
  return scrollsAlong(element, axis) || clipsAlong(element, axis)
}

export function confinesOverflowAlong(element: Element, axis: ScrollAxis): boolean {
  return clipsOrScrollsAlong(element, axis) && overflowsAlong(element, axis)
}

export function isPageRoot(element: Element): boolean {
  return element === document.documentElement || element === document.body
}

export function pageClipsAlong(axis: ScrollAxis): boolean {
  return clipsAlong(document.documentElement, axis) || clipsAlong(document.body, axis)
}

export function isFixedPositioned(element: Element): boolean {
  return getComputedStyle(element).position === "fixed"
}

export function isAbsolutelyPositioned(element: Element): boolean {
  return getComputedStyle(element).position === "absolute"
}

export function isStaticallyPositioned(element: Element): boolean {
  return getComputedStyle(element).position === "static"
}

export function isOutOfFlow(element: Element): boolean {
  return isFixedPositioned(element) || isAbsolutelyPositioned(element)
}

export function isLayered(element: Element): boolean {
  return isOutOfFlow(element) || isStickyPositioned(element)
}

export function scrollsRightToLeft(element: Element, axis: ScrollAxis): boolean {
  return axis.name === "horizontal" && getComputedStyle(element).direction === "rtl"
}

export function hasNoDisplay(element: Element): boolean {
  return getComputedStyle(element).display === "none"
}

function isScrollContainer(element: Element): boolean {
  const style = getComputedStyle(element)
  return SCROLL_CONTAINER_OVERFLOW_VALUES.has(style.overflowX) || SCROLL_CONTAINER_OVERFLOW_VALUES.has(style.overflowY)
}

export function overflowAlongPx(element: Element, axis: ScrollAxis): number {
  return element[axis.scrollSize] - element[axis.clientSize]
}

export function overflowsAlong(element: Element, axis: ScrollAxis): boolean {
  return overflowAlongPx(element, axis) > OVERFLOW_THRESHOLD_PX
}

export function hidesOwnContentAlong(element: Element, axis: ScrollAxis): boolean {
  return scrollsAlong(element, axis) && overflowsAlong(element, axis)
}

export function nearestScrollContainer(element: Element): Element | null {
  for (let ancestor = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (isScrollContainer(ancestor)) return ancestor
  }
  return null
}

export function lineHeightPx(element: Element): number {
  const style = getComputedStyle(element)
  return style.lineHeight === "normal"
    ? parseFloat(style.fontSize) * NORMAL_LINE_HEIGHT_TO_FONT_SIZE_RATIO
    : parseFloat(style.lineHeight)
}

export function isInlineLevel(element: Element): boolean {
  return getComputedStyle(element).display.startsWith("inline")
}

export function isStickyPositioned(element: Element): boolean {
  return getComputedStyle(element).position === "sticky"
}

export function hasOwnText(element: Element): boolean {
  return Array.from(element.childNodes).some((node) => node instanceof Text && node.data.trim().length > 0)
}

export function isRendered(element: Element): boolean {
  return element.getClientRects().length > 0
}

export function hasLaidOutBox(element: Element): boolean {
  const rect = element.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0
}

export function escapesClipOf(element: Element, clip: Element): boolean {
  if (isFixedPositioned(element)) return true
  if (!isAbsolutelyPositioned(element) || !(element instanceof HTMLElement)) return false
  const containingBlock = element.offsetParent
  return containingBlock === null || !(containingBlock === clip || clip.contains(containingBlock))
}

export function escapesClipOnTheWayTo(element: Element, clip: Element): boolean {
  for (let current: Element | null = element; current && current !== clip; current = current.parentElement) {
    if (escapesClipOf(current, clip)) return true
  }
  return false
}

export function isVisibilityHidden(element: Element): boolean {
  return getComputedStyle(element).visibility !== "visible"
}

export function formsContainingBlock(element: Element): boolean {
  return !DISPLAYS_THAT_FORM_NO_CONTAINING_BLOCK.has(getComputedStyle(element).display)
}

export function hasNoBoxOfItsOwn(element: Element): boolean {
  return getComputedStyle(element).display === "contents"
}

export function layoutChildren(element: Element): Element[] {
  return Array.from(element.children).flatMap((child) => (hasNoBoxOfItsOwn(child) ? layoutChildren(child) : [child]))
}

export function layoutParent(element: Element): Element | null {
  let parent = element.parentElement
  while (parent && hasNoBoxOfItsOwn(parent)) parent = parent.parentElement
  return parent
}

export function layoutRect(element: Element): DOMRect {
  if (!hasNoBoxOfItsOwn(element)) return element.getBoundingClientRect()
  const childRects = layoutChildren(element)
    .filter(isRendered)
    .map((child) => child.getBoundingClientRect())
  if (childRects.length === 0) return element.getBoundingClientRect()
  const left = Math.min(...childRects.map((rect) => rect.left))
  const top = Math.min(...childRects.map((rect) => rect.top))
  const right = Math.max(...childRects.map((rect) => rect.right))
  const bottom = Math.max(...childRects.map((rect) => rect.bottom))
  return new DOMRect(left, top, right - left, bottom - top)
}
